import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {getplantsAll} from '../../../../../../services/plantsService'
import {getUnitBranchAllSimple} from '../../../../../../services/unitBranchService'
import {getUsersAllRole} from '../../../../../../services/usersService'
import {useToast} from '../../../../../components/Toats'
import {ListPlantsLinked} from '../listPlantsLinked'
import {ClientAvatarUpload} from '../ClientAvatarUpload'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import {ModalAddUpdateUser} from '../../../../../users/components/ModalAddEditUser'
import {formatTelephone, formatCellphone} from '../../../../../../utils/formats'
import {SelectGlobal} from '../../../../../components/SelectGlobal'
import {
  FieldError,
  FieldLabel,
  FormSection,
} from '../../../../../unit-consumer/components/ModalAddEditUnitConsumer/components/FormSection'
import ucStyles from '../../../../../unit-consumer/components/ModalAddEditUnitConsumer/modalUnitConsumer.module.css'
import styles from '../../modalClient.module.css'

type IconInputProps = {
  id: string
  icon: string
  value: string
  placeholder?: string
  type?: string
  maxLength?: number
  invalid?: boolean
  iconColor?: string
  onChange: (value: string) => void
}

const IconInput = ({
  id,
  icon,
  value,
  placeholder,
  type = 'text',
  maxLength,
  invalid,
  iconColor,
  onChange,
}: IconInputProps) => (
  <div
    className={`input-group input-group-solid ${styles.iconGroup} ${
      invalid ? styles.inputInvalid : ''
    }`}
  >
    <span className='input-group-text'>
      <i
        className={`bi ${icon} fs-5 ${iconColor ? '' : 'text-reset'}`}
        style={iconColor ? {color: iconColor} : undefined}
      ></i>
    </span>
    <input
      id={id}
      type={type}
      className='form-control form-control-solid'
      placeholder={placeholder}
      value={value}
      maxLength={maxLength}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
)

/** Mantém só o username: tira URL do perfil, "@", espaços e caracteres inválidos. */
const sanitizeInstagram = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^(www\.)?instagram\.com\//i, '')
    .split(/[/?#]/)[0]
    .replace(/^@+/, '')
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, '')
    .slice(0, 30)

const StepModal = ({hasError, formik, idClient, linkedUser}: any) => {
  const {toggleToast} = useToast()
  const [plants, setPlants] = useState<any[]>([])
  const [loadingPlants, setLoadingPlants] = useState(false)
  const [showCreateAppModal, setShowCreateAppModal] = useState(false)
  const [myUser, setMyUser] = useState<any | null>(null)
  const [createdUser, setCreatedUser] = useState<any | null>(null)

  const idUnitBranch = formik.values.idUnitBranch || ''
  const showError = (value: any) => hasError && !value

  const {data: unitBranchs = [], isLoading: loadingUnitBranchs} = useQuery({
    queryKey: ['getUnitBranchsClientModal'],
    queryFn: async () => {
      try {
        return (await getUnitBranchAllSimple()) || []
      } catch (error) {
        console.log(error)
        toggleToast('Erro ao buscar a lista de unidades. ', 'error')
        return []
      }
    },
    staleTime: 1000 * 60 * 5,
  })

  const {
    data: users = [],
    isLoading: loadingUsers,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['getUsersClientRole', idUnitBranch],
    queryFn: async () => {
      try {
        return (await getUsersAllRole('CLIENTE', idUnitBranch)) || []
      } catch (error) {
        console.log(error)
        toggleToast('Erro ao buscar a lista de usuarios. ', 'error')
        return []
      }
    },
    staleTime: 1000 * 60 * 2,
  })

  // O usuário vinculado (ou recém-criado) pode não vir na lista filtrada pela filial
  const usersOptions = [createdUser, linkedUser].reduce(
    (list: any[], extra: any) =>
      extra?.id && !list.some((user: any) => user.id === extra.id) ? [extra, ...list] : list,
    users
  )

  const getPlants = async () => {
    try {
      setLoadingPlants(true)
      const response = await getplantsAll(
        `?unitBranch=${formik.values.idUnitBranch ? formik.values.idUnitBranch : ''}`
      )
      const linked: string[] = formik.values.idsPlants || []
      // usinas já vinculadas no topo
      const sorted = [...(response || [])].sort(
        (a: any, b: any) => Number(linked.includes(b.id)) - Number(linked.includes(a.id))
      )
      setPlants(sorted)
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao buscar a lista de usinas. ', 'error')
    } finally {
      setLoadingPlants(false)
    }
  }

  useEffect(() => {
    getPlants()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.idUnitBranch])

  const openCreateUser = () => {
    setMyUser({
      name: formik.values.nameResponsible || null,
      email: formik.values.email || null,
      role: 'CLIENTE',
      idUnitBranch: formik.values.idUnitBranch || null,
      idClient: idClient || null,
    })
    setShowCreateAppModal(true)
  }

  const linkedCount = (formik.values.idsPlants || []).length

  return (
    <div className={styles.grid}>
      <div className={styles.column}>
        <FormSection
          icon='/media/icons/duotune/communication/com006.svg'
          title='Dados do cliente'
          description='Foto, identificação e contato'
        >
          <div className={styles.identity}>
            <ClientAvatarUpload
              imageKey={formik.values.image}
              name={formik.values.nameResponsible}
              onChange={(key) => formik.setFieldValue('image', key)}
            />

            <div className={styles.identityFields}>
              <div>
                <FieldLabel htmlFor='client_name' required>
                  Nome
                </FieldLabel>
                <IconInput
                  id='client_name'
                  icon='bi-person'
                  placeholder='Nome do cliente'
                  value={formik.values.nameResponsible || ''}
                  invalid={showError(formik.values.nameResponsible)}
                  onChange={(value) => formik.setFieldValue('nameResponsible', value)}
                />
                <FieldError show={showError(formik.values.nameResponsible)}>
                  Informe um nome para o cliente
                </FieldError>
              </div>

              <div>
                <FieldLabel htmlFor='client_email' required>
                  E-mail
                </FieldLabel>
                <IconInput
                  id='client_email'
                  type='email'
                  icon='bi-envelope'
                  placeholder='nome@exemplo.com'
                  value={formik.values.email || ''}
                  invalid={showError(formik.values.email)}
                  onChange={(value) => formik.setFieldValue('email', value)}
                />
                <FieldError show={showError(formik.values.email)}>
                  O e-mail é obrigatório
                </FieldError>
              </div>
            </div>
          </div>

          <div className={styles.fieldsGrid2}>
            <div>
              <FieldLabel htmlFor='client_cpf' required hint='CPF ou CNPJ do responsável'>
                CPF/CNPJ
              </FieldLabel>
              <IconInput
                id='client_cpf'
                icon='bi-person-vcard'
                placeholder='000.000.000-00'
                value={formik.values.cpfResponsible || ''}
                invalid={showError(formik.values.cpfResponsible)}
                onChange={(value) => formik.setFieldValue('cpfResponsible', value)}
              />
              <FieldError show={showError(formik.values.cpfResponsible)}>
                Informe o CPF/CNPJ
              </FieldError>
            </div>

            <div>
              <FieldLabel
                htmlFor='client_instagram'
                hint='Usado para localizar as conversas do cliente pelo Instagram na central de atendimento'
              >
                Instagram
              </FieldLabel>
              <IconInput
                id='client_instagram'
                icon='bi-instagram'
                iconColor='#E1306C'
                placeholder='@usuario'
                value={formik.values.instagram ? `@${formik.values.instagram}` : ''}
                onChange={(value) => formik.setFieldValue('instagram', sanitizeInstagram(value) || null)}
              />
            </div>

            <div>
              <FieldLabel htmlFor='client_telephone'>Telefone</FieldLabel>
              <IconInput
                id='client_telephone'
                icon='bi-telephone'
                placeholder='(00) 0000-0000'
                maxLength={14}
                value={formik.values.telephone || ''}
                onChange={(value) => formik.setFieldValue('telephone', formatTelephone(value))}
              />
            </div>

            <div>
              <FieldLabel htmlFor='client_cellphone'>Celular / WhatsApp</FieldLabel>
              <IconInput
                id='client_cellphone'
                icon='bi-whatsapp'
                iconColor='#25D366'
                placeholder='(00) 00000-0000'
                maxLength={15}
                value={formik.values.cellphone || ''}
                onChange={(value) => formik.setFieldValue('cellphone', formatCellphone(value))}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          icon='/media/icons/duotune/general/gen001.svg'
          title='Vínculos'
          description='Filial responsável e acesso do cliente ao portal'
        >
          <div className={styles.fieldBlock}>
            <FieldLabel required hint='A filial define quais usinas e usuários ficam disponíveis'>
              Unidade (filial)
            </FieldLabel>
            <div className={showError(formik.values.idUnitBranch) ? styles.inputInvalid : ''}>
              <SelectGlobal
                itens={unitBranchs}
                buttonAll={false}
                textSearch='Buscar filial'
                textButtonInit='Selecione a filial'
                labelKey='companyName'
                valueKey='id'
                typeIcon='svg'
                linkIcon='/media/icons/duotune/general/gen001.svg'
                sizeIconSvg='2'
                value={formik.values.idUnitBranch || ''}
                loading={loadingUnitBranchs}
                className='w-100'
                onChange={(value: string) => {
                  formik.setFieldValue('idUnitBranch', value || null)
                }}
              />
            </div>
            <FieldError show={showError(formik.values.idUnitBranch)}>Selecione a filial</FieldError>
          </div>

          <div className={styles.fieldBlock}>
            <FieldLabel hint='Usuário que acessa o portal do cliente'>Usuário de acesso</FieldLabel>
            <div className={styles.userRow}>
              <div className={styles.userSelect}>
                <SelectGlobal
                  itens={usersOptions}
                  buttonAll={false}
                  textSearch='Buscar usuário'
                  textButtonInit='Selecione o usuário'
                  labelKey='name'
                  valueKey='id'
                  typeIcon='svg'
                  linkIcon='/media/icons/duotune/communication/com006.svg'
                  sizeIconSvg='2'
                  value={formik.values.idUser || ''}
                  loading={loadingUsers}
                  className='w-100'
                  onChange={(value: string) => {
                    formik.setFieldValue('idUser', value || null)
                  }}
                />
              </div>
              <button
                type='button'
                className='btn btn-light-primary flex-shrink-0 text-nowrap'
                onClick={openCreateUser}
                title='Criar novo usuário para o cliente'
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Novo
              </button>
            </div>
          </div>
        </FormSection>
      </div>

      <div className={styles.columnRight}>
        <section className={`${ucStyles.section} ${styles.plantsCard}`}>
          <header className={ucStyles.sectionHeader}>
            <span className={ucStyles.sectionIcon}>
              <KTSVG
                path='/media/icons/duotune/general/gen002.svg'
                className='svg-icon-2 svg-icon-primary'
              />
            </span>
            <div>
              <h4 className={ucStyles.sectionTitle}>Usinas vinculadas</h4>
              <span className={ucStyles.sectionDescription}>
                Selecione as usinas que pertencem ao cliente
              </span>
            </div>
            <div className={ucStyles.sectionExtra}>
              <span className={styles.counter}>
                {linkedCount} {linkedCount === 1 ? 'selecionada' : 'selecionadas'}
              </span>
            </div>
          </header>

          <div className={styles.plantsBody}>
            <ListPlantsLinked plants={plants} formik={formik} loading={loadingPlants} />
          </div>
        </section>
      </div>

      <ModalAddUpdateUser
        show={showCreateAppModal}
        id={myUser ? myUser.id : undefined}
        data={myUser ? myUser : undefined}
        handleClose={() => {
          setMyUser(null)
          setShowCreateAppModal(false)
          refetchUsers()
        }}
        onSuccess={(result: any) => {
          if (!result?.id) return
          setCreatedUser(result)
          formik.setFieldValue('idUser', result.id)
        }}
      />
    </div>
  )
}

export {StepModal}
